import {
  Box,
  Button,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import type { PayoutStatementView } from '@/api/generated';
import { asChargeStatus } from '@/api/resellerAdmin';
import { EmptyState } from '@/components/EmptyState';
import { moneyIn } from '@/components/moneyIn';
import { StatusChip } from '@/components/status/StatusChip';
import { chargedPayoutStatus } from '@/components/status/statusTones';
import { DATE_ONLY, formatCivilOrDash, formatInstantOrDash } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme';
import { settlementMonth } from './statementMath';
import { hasRetained, splitOf } from './payoutStatus';

export interface PayoutTableProps {
  statements: PayoutStatementView[];
  selectedId: number | undefined;
  /**
   * The query string that opens a settlement's ledger (`?settlement=<id>`, the list's other params
   * kept). Its month is a link there (a history entry, so Back closes it); a click elsewhere on the
   * row does the same.
   */
  openSearch: (id: number) => string;
  /** History state for the month link (TMMRSL-0074). */
  openState?: unknown;
  /**
   * Clears the active status filter. Given only while a filter is on: an empty table then says
   * nothing matches and offers "Show all", distinct from "none yet" (STATE-004).
   */
  onShowAll?: (() => void) | undefined;
}

/** A payout date: a bare civil date as received, else an instant's day in the viewer's zone. */
function instantOrCivil(value: string | undefined, locale: string, fallback: string): string {
  if (value?.length === 10) return formatCivilOrDash(value, locale, fallback);
  return formatInstantOrDash(value, locale, '', DATE_ONLY) || formatCivilOrDash(value, locale, fallback);
}

export function PayoutTable({
  statements,
  selectedId,
  openSearch,
  openState,
  onShowAll,
}: PayoutTableProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const navigate = useNavigate();
  const dash = t('statements.unavailable');

  if (statements.length === 0) {
    return (
      <Paper sx={{ px: 2.5 }}>
        {onShowAll ? (
          <EmptyState
            kind="noMatch"
            title={t('payouts.filteredEmpty')}
            action={<Button onClick={onShowAll}>{t('payouts.showAll')}</Button>}
          />
        ) : (
          <EmptyState kind="none" title={t('payouts.empty')} />
        )}
      </Paper>
    );
  }

  return (
    <Paper sx={{ overflowX: 'auto' }}>
      <Table size="small" aria-label={t('payouts.statementsTitle')}>
        <TableHead>
          <TableRow>
            <TableCell>{t('statements.col.month')}</TableCell>
            <TableCell>{t('money.currency')}</TableCell>
            <TableCell align="right">{t('statements.col.retail')}</TableCell>
            <TableCell align="right">{t('statements.col.wholesale')}</TableCell>
            <TableCell align="right">{t('statements.col.margin')}</TableCell>
            <TableCell align="right">{t('statements.col.aff')}</TableCell>
            <TableCell align="right">{t('statements.col.carriedIn')}</TableCell>
            <TableCell align="right">{t('statements.col.net')}</TableCell>
            <TableCell>{t('statements.col.status')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {statements.map((stmt) => {
            // Each settlement in its own currency — the table never adds rows together.
            const currency = stmt.currency;
            // Optional margin, affiliate commission, and net dash when absent (RPT-005). A negative keeps Intl's sign.
            const money = (minor: number | undefined) =>
              minor === undefined ? dash : moneyIn(minor, currency, locale, dash);
            const selected = stmt.id === selectedId;
            const transferWarning =
              stmt.transferStatus === 'FAILED' || stmt.transferStatus === 'REJECTED';
            const month = settlementMonth(stmt.periodStart, locale, dash);
            const id = stmt.id;
            return (
              <TableRow
                key={id}
                hover
                selected={selected}
                // A mouse convenience: exactly what the month's link does. The open row is a no-op.
                onClick={() => {
                  if (selected) return;
                  void navigate({ search: openSearch(id) }, { state: openState });
                }}
                sx={{
                  cursor: 'pointer',
                  bgcolor: selected ? tokens.brandSoft : undefined,
                }}
              >
                <TableCell component="th" scope="row">
                  <Link
                    id={`settlement-month-${id}`}
                    component={RouterLink}
                    to={{ search: openSearch(id) }}
                    state={openState}
                    aria-current={selected ? 'true' : undefined}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (selected) event.preventDefault();
                    }}
                  >
                    {month}
                  </Link>
                </TableCell>
                <TableCell>{currency || dash}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {money(stmt.grossMinor)}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', color: tokens.text2 }}>
                  {money(stmt.wholesaleMinor)}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {money(stmt.resellerMarginMinor)}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {money(stmt.affiliateCommissionMinor)}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {money(stmt.carriedInMinor)}
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                  {money(stmt.netMinor)}
                  {stmt.carriedInMinor !== 0 ? (
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', color: tokens.text2, fontWeight: 400 }}
                    >
                      {t('statements.carried', { amount: money(stmt.carriedInMinor) })}
                    </Typography>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                    <PayoutStatusCell stmt={stmt} locale={locale} />
                    {stmt.hold ? <StatusChip domain="hold" status={stmt.hold} /> : null}
                    {transferWarning ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <StatusChip domain="transfer" status={stmt.transferStatus} />
                        {stmt.failureCode ? (
                          <Typography variant="mono" component="span">
                            {t('statements.transfer.reference', { code: stmt.failureCode })}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : null}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {statements.some(hasRetained) ? (
        <Typography variant="caption" component="p" sx={{ color: 'text.secondary', px: 2, py: 1.5 }}>
          {t('payouts.retainedNote')}
        </Typography>
      ) : null}
    </Paper>
  );
}

/**
 * The Status cell. A month split at deactivation (TMMAPI-0116) shows both outcomes, the retained
 * one and the normal part's, with a caption; any other row shows its one status. The server's
 * `note` text is never shown (TMM-B3).
 */
function PayoutStatusCell({ stmt, locale }: { stmt: PayoutStatementView; locale: string }) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const split = splitOf(stmt);
  if (!split) return <PayoutStatus stmt={stmt} status={stmt.payoutStatus} locale={locale} />;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
        <StatusChip domain="payout" status="RETAINED" />
        <PayoutStatus
          stmt={stmt}
          status={split.normal.status}
          netMinor={split.normal.netMinor}
          locale={locale}
        />
      </Box>
      <Typography variant="caption" sx={{ color: tokens.text2 }}>
        {t('payouts.split.caption')}
      </Typography>
    </Box>
  );
}

/** One settlement status, always in words (STATE-001/002). An unknown status is "—". */
export function PayoutStatus({
  stmt,
  status,
  netMinor = stmt.netMinor,
  locale,
}: {
  stmt: PayoutStatementView;
  status: PayoutStatementView['payoutStatus'];
  /** The net a CHARGED status falls back to: the row's, or a split row's normal part's. */
  netMinor?: number | undefined;
  locale: string;
}) {
  const { t } = useTranslation();
  const dash = t('statements.unavailable');
  if (status === 'CHARGED') {
    // A negative month charged to the reseller's card. The amount is the charge's, else the net.
    // The tone follows the charge; the words may still say "Charged" because Net carries the sign.
    const charge = asChargeStatus(stmt.charge?.status);
    const chargeLabel = charge ? t(`charges.status.${charge}`) : dash;
    const amountMinor = stmt.charge?.amountMinor ?? netMinor;
    const hasAmount = amountMinor !== undefined;
    return (
      <StatusChip
        domain="payout"
        status={chargedPayoutStatus(charge, hasAmount)}
        values={
          hasAmount
            ? {
                amount: moneyIn(Math.abs(amountMinor), stmt.currency, locale, dash),
                status: chargeLabel,
              }
            : { status: chargeLabel }
        }
      />
    );
  }
  if (status === 'PAID') {
    return (
      <StatusChip
        domain="payout"
        status="PAID"
        values={{
          date: instantOrCivil(stmt.payoutAt, locale, dash),
          ref: stmt.payoutRef ?? dash,
        }}
      />
    );
  }
  if (status === 'PENDING') {
    return (
      <StatusChip
        domain="payout"
        status="PENDING"
        values={{ closes: formatCivilOrDash(stmt.periodEnd, locale, dash) }}
      />
    );
  }
  return <StatusChip domain="payout" status={status} />;
}
